import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, requestPasswordReset, resendMfa } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button, Icon } from '../components/ui';
import type { MfaChallenge, MfaMethod } from '../lib/types';
import './Login.css';

type View = 'signin' | 'mfa' | 'reset' | 'reset_sent';

/** Structured detail the API attaches to a lockout or suspension. */
interface BlockedDetail {
  code?: string;
  message?: string;
  unlocksAt?: string;
  unlocksInSeconds?: number;
  suspendedBy?: { id: string; name: string; level: string } | null;
  note?: string;
  reference?: string;
}

/** What the second-factor screen is called, per factor. */
const MFA_TITLE: Record<MfaMethod, string> = {
  totp: 'Open your authenticator',
  sms: 'Check your phone',
  email: 'Check your email',
};

const MFA_ALTERNATIVE: Record<MfaMethod, string> = {
  totp: 'Use my authenticator app',
  sms: 'Text me a code',
  email: 'Email me a code',
};

export function Login() {
  const { signIn, completeMfa, expired } = useAuth();
  const navigate = useNavigate();

  const [view, setView] = useState<View>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<BlockedDetail | null>(null);

  const [challenge, setChallenge] = useState<MfaChallenge | null>(null);
  const [code, setCode] = useState('');
  /** Recovery codes are longer than six digits and get their own field. */
  const [usingRecovery, setUsingRecovery] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [switching, setSwitching] = useState(false);

  function handleError(err: unknown) {
    if (err instanceof ApiError) {
      const body = err.body as BlockedDetail | null;
      // A lockout or a suspension is its own screen, not an inline message —
      // each one has to say what happened and who can fix it.
      if (body?.code === 'account_locked' || body?.code === 'access_suspended') {
        setBlocked(body);
        setError(null);
        return;
      }
      setError(err.message);
      return;
    }
    setError('Something went wrong. Try again.');
  }

  async function onSignIn(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setBlocked(null);
    try {
      const res = await signIn(email, password, remember);
      if (res.status === 'mfa_required') {
        setChallenge(res);
        setCode('');
        setUsingRecovery(false);
        setRecoveryCode('');
        setView('mfa');
      } else {
        navigate('/', { replace: true });
      }
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      const res = await completeMfa(
        challenge.challengeId,
        usingRecovery ? recoveryCode : code,
        remember,
      );
      if (res.status === 'ok') {
        // A freshly invited account has no factor yet; the shell sends it to
        // setup. Everyone else lands where they were going.
        navigate('/', { replace: true });
      }
    } catch (err) {
      handleError(err);
    } finally {
      setBusy(false);
    }
  }

  /** "Try another way" — the same half-finished sign-in, different factor. */
  async function switchTo(method?: MfaMethod) {
    if (!challenge) return;
    setSwitching(true);
    setError(null);
    try {
      const next = await resendMfa(challenge.challengeId, method);
      if (next.status === 'mfa_required') {
        setChallenge(next);
        setCode('');
        setUsingRecovery(false);
      }
    } catch (err) {
      handleError(err);
    } finally {
      setSwitching(false);
    }
  }

  async function onReset(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await requestPasswordReset(email);
      setView('reset_sent');
    } catch {
      // The endpoint always accepts, so treat a transport failure the same way
      // rather than leaking whether the address exists.
      setView('reset_sent');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <section className="login__panel">
        <div className="login__inner">
          <div className="login__brand">
            <span className="login__mark">{Icon.logo({ size: 18 })}</span>
            <span className="login__brandname">Fairmount</span>
          </div>

          {blocked ? (
            <BlockedPanel
              detail={blocked}
              onBack={() => {
                setBlocked(null);
                setPassword('');
              }}
            />
          ) : view === 'signin' ? (
            <form className="login__form" onSubmit={onSignIn}>
              <h1 className="login__title">Sign in</h1>
              <p className="login__lede">
                The agent portal for ISOs and agents writing for Fairmount.
              </p>

              {expired && (
                <div className="notice notice--info" role="status">
                  {Icon.clock({ size: 15 })}
                  <span>
                    You were signed out after 30 minutes idle. Any account that can open
                    a merchant application times out, and your office cannot extend it.
                  </span>
                </div>
              )}

              {error && (
                <div className="notice notice--danger" role="alert">
                  {Icon.alert({ size: 15 })}
                  <span>{error}</span>
                </div>
              )}

              <div className="field">
                <label className="field__label" htmlFor="email">
                  Work email
                </label>
                <input
                  id="email"
                  className="input input--lg"
                  type="email"
                  autoComplete="username"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="field">
                <label className="field__label" htmlFor="password">
                  Password
                </label>
                <div className="password">
                  <input
                    id="password"
                    className={`input input--lg ${error ? 'input--error' : ''}`}
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="password__toggle"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              <div className="login__row">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                  />
                  Remember this device
                </label>
                <span className="spacer" />
                <button
                  type="button"
                  className="linkbtn"
                  onClick={() => {
                    setView('reset');
                    setError(null);
                  }}
                >
                  Forgot password?
                </button>
              </div>

              <Button type="submit" variant="primary" size="lg" block disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </Button>

              <p className="login__hint">
                Seeded accounts use the password{' '}
                <code className="mono">Portal!2026</code> — try{' '}
                <button
                  type="button"
                  className="linkbtn"
                  onClick={() => setEmail('dana@keystonegrp.com')}
                >
                  dana@keystonegrp.com
                </button>{' '}
                (L3) or{' '}
                <button
                  type="button"
                  className="linkbtn"
                  onClick={() => setEmail('marcus@keystonegrp.com')}
                >
                  marcus@keystonegrp.com
                </button>{' '}
                (L4).
              </p>
            </form>
          ) : view === 'mfa' ? (
            <form className="login__form" onSubmit={onVerify}>
              <button
                type="button"
                className="linkbtn linkbtn--back"
                onClick={() => setView('signin')}
              >
                {Icon.chevronLeft({ size: 13 })} Back
              </button>
              <h1 className="login__title">{MFA_TITLE[challenge?.method ?? 'totp']}</h1>
              <p className="login__lede">
                {usingRecovery ? (
                  <>
                    Enter one of the recovery codes you saved when you set up two-factor.
                    Each one works once.
                  </>
                ) : challenge?.method === 'totp' ? (
                  <>
                    Open your authenticator app and enter the six-digit code for{' '}
                    <strong>Fairmount Payments</strong>.
                  </>
                ) : (
                  <>
                    We sent a six-digit code to <strong>{challenge?.sentTo}</strong>. It
                    expires in {Math.round((challenge?.expiresInSeconds ?? 600) / 60)}{' '}
                    minutes.
                  </>
                )}
              </p>

              {error && (
                <div className="notice notice--danger" role="alert">
                  {Icon.alert({ size: 15 })}
                  <span>{error}</span>
                </div>
              )}

              {usingRecovery ? (
                <div className="field">
                  <label className="field__label" htmlFor="recovery">
                    Recovery code
                  </label>
                  <input
                    id="recovery"
                    className="input input--lg mono"
                    autoComplete="one-time-code"
                    spellCheck={false}
                    placeholder="xxxx-xxxx-xxxx"
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value)}
                  />
                </div>
              ) : (
                <CodeInput value={code} onChange={setCode} />
              )}

              {challenge?.devCode && (
                <div className="notice notice--info">
                  {Icon.alert({ size: 15 })}
                  <span>
                    Development build — no{' '}
                    {challenge.method === 'sms' ? 'SMS account' : 'mail relay'} is
                    configured, so the code is shown here:{' '}
                    <strong className="mono">{challenge.devCode}</strong>.
                  </span>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                block
                disabled={
                  busy || (usingRecovery ? recoveryCode.length < 8 : code.length !== 6)
                }
              >
                {busy ? 'Verifying…' : 'Verify and sign in'}
              </Button>

              <div className="login__alternatives">
                {!usingRecovery && challenge?.method !== 'totp' && (
                  <button
                    type="button"
                    className="linkbtn"
                    disabled={switching}
                    onClick={() => void switchTo(challenge?.method)}
                  >
                    Send it again
                  </button>
                )}
                {!usingRecovery &&
                  (challenge?.alternatives ?? []).map((alt) => (
                    <button
                      key={alt.method}
                      type="button"
                      className="linkbtn"
                      disabled={switching}
                      onClick={() => void switchTo(alt.method)}
                    >
                      {MFA_ALTERNATIVE[alt.method]}
                      {alt.sentTo ? ` (${alt.sentTo})` : ''}
                    </button>
                  ))}
                {(challenge?.recoveryCodesRemaining ?? 0) > 0 && (
                  <button
                    type="button"
                    className="linkbtn"
                    onClick={() => {
                      setUsingRecovery(!usingRecovery);
                      setError(null);
                    }}
                  >
                    {usingRecovery ? 'Use a code instead' : 'Use a recovery code'}
                  </button>
                )}
              </div>

              <p className="login__footnote">
                Two-factor is required on every account with access to merchant
                applications, and cannot be switched off by your office.
              </p>
            </form>
          ) : view === 'reset' ? (
            <form className="login__form" onSubmit={onReset}>
              <button
                type="button"
                className="linkbtn linkbtn--back"
                onClick={() => setView('signin')}
              >
                {Icon.chevronLeft({ size: 13 })} Back to sign in
              </button>
              <h1 className="login__title">Reset your password</h1>
              <p className="login__lede">
                Enter the email you sign in with and we will send a reset link. It is
                valid for 30 minutes and can be used once.
              </p>

              <div className="field">
                <label className="field__label" htmlFor="reset-email">
                  Work email
                </label>
                <input
                  id="reset-email"
                  className="input input--lg"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <Button type="submit" variant="primary" size="lg" block disabled={busy}>
                Send reset link
              </Button>

              <p className="login__footnote">
                If your account uses company SSO, the reset happens with your identity
                provider and this link will not arrive.
              </p>
            </form>
          ) : (
            <div className="login__form">
              <span className="login__icon login__icon--ok">{Icon.check({ size: 21 })}</span>
              <h1 className="login__title">Check your inbox</h1>
              <p className="login__lede">
                If that address has an account, a reset link is on its way. It is valid
                for 30 minutes and can be used once.
              </p>
              <Button variant="secondary" size="lg" block onClick={() => setView('signin')}>
                Back to sign in
              </Button>
            </div>
          )}

          <p className="login__legal">
            Fairmount Payments is a registered agent of [SPONSOR BANK, CITY, STATE].
            Access is monitored and logged. © 2026 Fairmount Payments.
          </p>
        </div>
      </section>

      <aside className="login__aside">
        <div className="login__status">
          <span className="dot" />
          All systems operational
        </div>
        <div>
          <h2 className="login__pitch">Smarter payment solutions for ISOs and agents.</h2>
          <ul className="login__points">
            <li>{Icon.check({ size: 15 })} Lifetime residuals, generous splits, clear terms</li>
            <li>{Icon.check({ size: 15 })} Same-day payments and fast digital onboarding</li>
            <li>{Icon.check({ size: 15 })} Recruit sub-agents — splits handled for you</li>
          </ul>
        </div>
        <div className="login__preview">
          <div className="login__previewtile">
            <span>Applications in flight</span>
            <strong>12</strong>
          </div>
          <div className="login__previewtile">
            <span>Approved this month</span>
            <strong>8</strong>
          </div>
          <div className="login__previewtile">
            <span>Residual · August</span>
            <strong>$18,430</strong>
          </div>
        </div>
      </aside>
    </div>
  );
}

/**
 * Lockout and suspension. Each states what happened, what it costs, and who
 * can undo it — naming the node that acted, so nobody opens a ticket with the
 * wrong party.
 */
function BlockedPanel({
  detail,
  onBack,
}: {
  detail: BlockedDetail;
  onBack: () => void;
}) {
  const locked = detail.code === 'account_locked';
  const [left, setLeft] = useState(detail.unlocksInSeconds ?? 0);

  useEffect(() => {
    if (!locked || left <= 0) return undefined;
    const t = window.setInterval(() => setLeft((s) => Math.max(0, s - 1)), 1000);
    return () => window.clearInterval(t);
  }, [locked, left]);

  const mmss = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, '0')}`;

  return (
    <div className="login__form">
      <span className={`login__icon login__icon--${locked ? 'warn' : 'neutral'}`}>
        {locked ? Icon.permissions({ size: 21 }) : Icon.underwriting({ size: 21 })}
      </span>

      <h1 className="login__title">
        {locked ? 'Account locked for 15 minutes' : 'Your access is suspended'}
      </h1>
      <p className="login__lede">{detail.message}</p>

      {locked && (
        <div className="countdown">
          {Icon.clock({ size: 15 })}
          <span>Unlocks in</span>
          <strong className="mono">{left > 0 ? mmss : 'now'}</strong>
        </div>
      )}

      {detail.note && (
        <div className="notice notice--ok">
          {Icon.check({ size: 15 })}
          <span>{detail.note}</span>
        </div>
      )}

      <div className="login__actions">
        <Button variant="primary" size="lg" onClick={onBack} block={!locked}>
          {locked ? 'Back to sign in' : 'Back to sign in'}
        </Button>
        {locked && (
          <Button variant="secondary" size="lg">
            Email me a reset link
          </Button>
        )}
      </div>

      {detail.suspendedBy && (
        <Button variant="secondary" size="lg" block>
          Contact {detail.suspendedBy.name}
        </Button>
      )}

      <p className="login__footnote">
        {locked
          ? 'A manager can clear a lock but never see or set the password.'
          : 'Fairmount support cannot override a parent office.'}
        {detail.reference && (
          <>
            {' '}
            Reference <span className="mono">{detail.reference.slice(0, 8)}</span>
          </>
        )}
      </p>
    </div>
  );
}

/** Six separate boxes, as drawn — with paste and backspace behaving sanely. */
function CodeInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = value.padEnd(6, ' ').slice(0, 6).split('');

  function setDigit(i: number, d: string) {
    const next = value.padEnd(6, ' ').split('');
    next[i] = d || ' ';
    onChange(next.join('').replace(/\s/g, ''));
  }

  return (
    <fieldset className="code">
      <legend className="visually-hidden">Six digit verification code</legend>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          className="code__box"
          type="text"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          aria-label={`Digit ${i + 1}`}
          value={d.trim()}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, '').slice(-1);
            setDigit(i, v);
            if (v && i < 5) refs.current[i + 1]?.focus();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !d.trim() && i > 0) {
              refs.current[i - 1]?.focus();
            }
          }}
          onPaste={(e) => {
            e.preventDefault();
            const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
            if (pasted) {
              onChange(pasted);
              refs.current[Math.min(pasted.length, 5)]?.focus();
            }
          }}
        />
      ))}
    </fieldset>
  );
}
